import { useMemo, useState } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  ButtonGroup,
  Spinner,
  ToggleButton,
} from "react-bootstrap";

import {
  APIKeySelect,
  APIKeySelectProps,
  APIRequestsTable,
  APIRequestTableColumn,
  CommonAPIRequestSummary,
  CommonBagelAPIKey,
  LocalStorageKeys,
  Page,
  PaginatedSearch,
  Paginator,
  useFilters,
  util,
} from "@bagel-web/common";
import {
  Fields,
  FieldType,
  FilterBar,
  FlexToolbarItem,
  Toolbar,
} from "@bagel-web/components";

type SearchFilters = {
  onlyFailed: boolean;
  agentId?: string;
  apiKeyId?: string;
  fromDateTime?: number;
  toDateTime?: number;
};

const apiRequestQueries = {
  // ``sessionPathMask`` doubles as the session-scope discriminator on the
  // query key: without it, navigating between sessions A → B with the same
  // filter state would serve back A's cached page (the per-parent
  // ``fetchAPIRequests`` closure differs, but react-query keys on the
  // ``queryKey`` value, not the function identity).
  search: (
    filters: PaginatedSearch<SearchFilters>,
    sessionPathMask: string | undefined
  ) => ["api_requests", "search", sessionPathMask, filters],
};

function SearchAPIRequests({
  sessionPathMask,
  showAgentId = false,
  persistFilters = true,
  getAPIRequestLink,
  fetchAPIKeys,
  fetchAPIRequests,
}: {
  // When the table is rendered inside a session context, pass the session
  // id so the path column can mask it out of the rendered API paths.
  // This is purely cosmetic: the session-scoped fetch is the caller's
  // responsibility (e.g. via a `fetchAPIRequests` closure that hits a
  // session-scoped endpoint), not something this component injects.
  sessionPathMask?: string;
  showAgentId?: boolean;
  persistFilters?: boolean;
  getAPIRequestLink: (apiRequest: CommonAPIRequestSummary) => string;
  fetchAPIKeys: (
    filters: PaginatedSearch<object>
  ) => Promise<Page<CommonBagelAPIKey>>;
  fetchAPIRequests: (
    filters: PaginatedSearch<SearchFilters>
  ) => Promise<Page<CommonAPIRequestSummary>>;
}) {
  const ComposedAPIKeySelect = useMemo(
    () => (props: Omit<APIKeySelectProps, "fetchAPIKeys">) => {
      return <APIKeySelect {...props} fetchAPIKeys={fetchAPIKeys} />;
    },
    [fetchAPIKeys]
  );

  const queryClient = useQueryClient();
  const filterFields: Fields = useMemo(
    () => [
      ...(showAgentId
        ? [
            {
              name: "agentId",
              label: "Agent ID",
              showMonoValue: true,
              type: FieldType.String,
              colSpan: 1,
            },
          ]
        : []),
      {
        name: "apiKeyId",
        label: "API Key",
        showMonoValue: true,
        Component: ComposedAPIKeySelect,
        type: FieldType.String,
        colSpan: showAgentId ? 1 : 2,
      },
      {
        name: "fromDateTime",
        label: "From",
        type: FieldType.DateTime,
        formatValue: (value) => util.formatDate(new Date(value as string)),
      },
      {
        name: "toDateTime",
        label: "To",
        type: FieldType.DateTime,
        formatValue: (value) => util.formatDate(new Date(value as string)),
      },
      {
        name: "onlyFailed",
        label: "Show Errors Only",
        type: FieldType.String,
        hidden: true,
      },
    ],
    [queryClient]
  );

  const { filterCriteria, handleSetFilters, handleClearFilter } = useFilters(
    filterFields,
    persistFilters ? LocalStorageKeys.Filters.API_REQUESTS : undefined
  );

  const search: SearchFilters = useMemo(() => {
    return {
      onlyFailed: filterCriteria["onlyFailed"]?.value === "true",
      agentId: showAgentId ? filterCriteria["agentId"]?.value : undefined,
      apiKeyId: filterCriteria["apiKeyId"]?.value,
      fromDateTime:
        filterCriteria.fromDateTime &&
        util.getTimestampFromDateString(filterCriteria.fromDateTime?.value),
      toDateTime:
        filterCriteria.toDateTime &&
        util.getTimestampFromDateString(filterCriteria.toDateTime?.value),
    };
  }, [filterCriteria, showAgentId]);

  const [paginatedSearch, setPaginatedSearch] = useState<
    PaginatedSearch<SearchFilters>
  >({
    ...search,
    startsAfter: null,
  });
  const [isFirstPage, setIsFirstPage] = useState(true);

  const {
    isPending,
    isError,
    data: apiRequests,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: apiRequestQueries.search(paginatedSearch, sessionPathMask),
    queryFn: () => fetchAPIRequests(paginatedSearch),
  });

  return (
    <>
      <Toolbar>
        <ButtonGroup>
          <ToggleButton
            type="radio"
            variant="outline-secondary"
            checked={filterCriteria["onlyFailed"]?.value !== "true"}
            onChange={() =>
              handleSetFilters({
                ...filterCriteria,
                onlyFailed: { value: "false" },
              })
            }
            id="show-result-all"
            value="all"
          >
            All
          </ToggleButton>
          <ToggleButton
            type="radio"
            variant="outline-secondary"
            checked={filterCriteria["onlyFailed"]?.value === "true"}
            onChange={() =>
              handleSetFilters({
                ...filterCriteria,
                onlyFailed: { value: "true" },
              })
            }
            id="show-result-only-errors"
            value="only-errors"
          >
            Only Errors
          </ToggleButton>
        </ButtonGroup>
        <FlexToolbarItem>
          <FilterBar
            criteria={filterCriteria}
            fields={filterFields}
            onSetFilters={handleSetFilters}
            onClear={handleClearFilter}
          />
        </FlexToolbarItem>
        <Button
          onClick={() => refetch()}
          disabled={!isFirstPage || isRefetching}
          title="Refresh"
        >
          {isRefetching && <Spinner size="sm" />}
          {!isRefetching && <i className="bi-arrow-clockwise" />}
        </Button>
      </Toolbar>

      {isPending && <Spinner />}
      {isError && (
        <Alert variant="warning">
          Could not load API requests: {error.message}
        </Alert>
      )}
      {!isPending && !isError && (
        <APIRequestsTable
          apiRequests={apiRequests?.items || []}
          columns={[
            APIRequestTableColumn.CREATED,
            APIRequestTableColumn.METHOD,
            APIRequestTableColumn.PATH,
            APIRequestTableColumn.API_KEY,
            APIRequestTableColumn.STATUS_CODE,
          ]}
          getAPIRequestLink={getAPIRequestLink}
          pathMasks={[
            ...(sessionPathMask ? [sessionPathMask] : []),
            ...(filterCriteria["agentId"]
              ? [filterCriteria["agentId"].value]
              : []),
          ]}
          onSetFilters={handleSetFilters}
        />
      )}

      <Paginator
        search={search}
        currentPage={apiRequests || null}
        onChangePaginatedSearch={({ paginatedSearch, isFirstPage }) => {
          setPaginatedSearch(paginatedSearch);
          setIsFirstPage(isFirstPage);
        }}
      />
    </>
  );
}

export default SearchAPIRequests;
